import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import PremiumHeroSection from "@/components/PremiumHeroSection";
import { api, PLACEHOLDER_IMAGE, resolveMediaUrl } from "@/api";
import {
  Search, ShoppingCart, ChevronRight, Heart, Star, Gavel, Smartphone, Laptop, Watch, Tablet,
  Headphones, Gamepad2, Camera, LayoutGrid, Percent, Clock, ShieldCheck, Wallet,
  RotateCcw, BadgeCheck, Truck, PackageOpen, Flame,
  ArrowRight, TrendingUp, RefreshCw, Wrench, Cpu, Phone, Globe, MessageCircle, Zap, Tag, HomeIcon,
} from "lucide-react";

const money = (v = 0) => `₹${Number(v).toLocaleString("en-IN")}`;
const rateFor = (id = "") => {
  const seed = [...String(id)].reduce((t, c) => t + c.charCodeAt(0), 0);
  return { stars: (4 + (seed % 9) / 10).toFixed(1), count: `${4 + (seed % 12)}.${seed % 9}K` };
};

const BADGE_COLORS = ["", "pink", "green", "amber", "purple", "pink", "green"];

function calcMrp(price, offStr) {
  if (!offStr) return null;
  const pct = parseInt(offStr.match(/\d+/)?.[0] || "0");
  if (pct <= 0 || pct >= 100) return null;
  return Math.round(price / (1 - pct / 100) / 100) * 100;
}
const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-40px" }, transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] } };
const fadeScale = { initial: { opacity: 0, scale: 0.95 }, whileInView: { opacity: 1, scale: 1 }, viewport: { once: true, margin: "-40px" }, transition: { duration: 0.5 } };
const staggerContainer = { whileInView: { transition: { staggerChildren: 0.06 } }, viewport: { once: true, margin: "-40px" } };
const staggerItem = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

const CATEGORIES = [
  { label: "Mobiles & Phones", slug: "mobiles", icon: Smartphone, sub: "Latest Models" },
  { label: "Mobile Parts", slug: "accessories", icon: Wrench, sub: "Display, Battery, ICs" },
  { label: "Accessories", slug: "accessories", icon: Headphones, sub: "Cases, Chargers, Cables" },
  { label: "Smart Watches", slug: "smartwatch", icon: Watch, sub: "Apple & Android" },
  { label: "Laptops & Computers", slug: "laptops", icon: Laptop, sub: "Work, Study, Gaming" },
  { label: "Gaming", slug: "gaming", icon: Gamepad2, sub: "Consoles & Accessories" },
  { label: "Tablets", slug: "tablets", icon: Tablet, sub: "iPad, Samsung, More" },
  { label: "Cameras", slug: "cameras", icon: Camera, sub: "Pro & Mirrorless" },
];
const HOT_CATEGORIES = [
  { label: "Mobile Parts", sub: "Display | Battery | IC", icon: Cpu, slug: "accessories" },
  { label: "Accessories", sub: "Cases | Chargers | Cables", icon: Headphones, slug: "accessories" },
  { label: "Smart Watches", sub: "Fitness | Health | Style", icon: Watch, slug: "smartwatch" },
  { label: "Refurbished Phones", sub: "Certified | Tested | Warranty", icon: RefreshCw, slug: "mobiles" },
  { label: "Compare Phones", sub: "Find the Perfect Match", icon: Smartphone, slug: "" },
  { label: "Sell Your Phone", sub: "Get Best Value", icon: TrendingUp, slug: "" },
];
const BADGES = ["Bestseller", "New Launch", "Hot Deal", "Assured", "Top Rated", "Value"];
const FEATURED_ORDER = ["iphone", "samsung", "macbook", "boat-airdopes-141", "pixel-7", "nothing-phone-2"];

/* ========== Countdown Timer ========== */
function AnimatedCountdown({ testId = "flash-countdown" }) {
  const [left, setLeft] = useState(12 * 3600 + 45 * 60 + 30);
  useEffect(() => { const t = setInterval(() => setLeft(v => v > 0 ? v - 1 : 12 * 3600), 1000); return () => clearInterval(t); }, []);
  const pad = v => String(v).padStart(2, "0");
  const h = pad(Math.floor(left / 3600)), m = pad(Math.floor((left % 3600) / 60)), s = pad(left % 60);
  return (
    <div className="countdown" data-testid={testId}>
      <motion.span key={`h-${h}`} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>{h}</motion.span>:
      <motion.span key={`m-${m}`} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>{m}</motion.span>:
      <motion.span key={`s-${s}`} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>{s}</motion.span>
    </div>
  );
}

/* ========== Reusable Sale Shelves ========== */
function SaleShelf({ id, title, eyebrow, icon: Icon, products, onAdd, to, tone = "cyan", showTimer = false }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [visible, setVisible] = useState(4);
  const touchStartX = useRef(null);

  // Responsive: 2 cards on mobile, 4 on desktop
  useEffect(() => {
    const update = () => setVisible(window.innerWidth < 600 ? 2 : 4);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useEffect(() => {
    if (paused || products.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % products.length), 5000);
    return () => clearInterval(t);
  }, [paused, products.length]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    setPaused(true);
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 35) {
      if (diff > 0) setIdx(i => (i + 1) % products.length);
      else setIdx(i => (i - 1 + products.length) % products.length);
    }
    touchStartX.current = null;
    setPaused(false);
  };

  // Duplicate for seamless looping
  const looped = products.length < visible ? [...products, ...products, ...products, ...products].slice(0, Math.max(visible * 2, products.length * 2)) : [...products, ...products];

  return (
    <motion.section
      {...fadeUp}
      className={`flash-wrap sale-shelf sale-shelf-${tone}`}
      data-testid={`${id}-section`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="section-title sale-shelf-title">
        <div className="sale-heading"><span className="sale-heading-icon"><Icon size={18} /></span><div><span className="sale-kicker">{eyebrow}</span><h2>{title}</h2></div></div>
        {showTimer && <div className="flash-timer" data-testid={`${id}-timer`}><Clock size={14} /> Ends in <AnimatedCountdown testId={`${id}-countdown`} /></div>}
        <Link to={to} data-testid={`view-${id}-link`}>View All <ChevronRight size={15} /></Link>
      </div>
      <div className="shelf-track-wrapper"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="shelf-track"
          style={{ transform: `translateX(-${idx * (100 / visible)}%)` }}
        >
          {looped.map((product, i) => (
            <div className="shelf-card-wrap" key={`${id}-${product.id}-${i}`}>
              <article className="flash-card" data-testid={`${id}-card-${product.id}`}>
                <div className="fc-top">
                  <span className={`fc-badge ${BADGE_COLORS[i % BADGE_COLORS.length]}`}>
                    {product.off ? product.off : "Deal"}
                  </span>
                  <button className="fc-heart" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}><Heart size={14} /></button>
                </div>
                <Link to={`/product/${product.id}`} data-testid={`${id}-product-${product.id}`}>
                  <div className="fc-img-wrap">
                    <img src={product.image || PLACEHOLDER_IMAGE} alt={product.name} onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMAGE; }} />
                  </div>
                </Link>
                <div className="fc-info">
                  <b className="fc-name">{product.name}</b>
                  <div className="fc-rating">★ {rateFor(product.id).stars} <span>({rateFor(product.id).count})</span></div>
                  <div className="fc-pricing">
                    <strong className="fc-price">{money(product.price)}</strong>
                    {(product.original_price || calcMrp(product.price, product.off)) && (
                      <s className="fc-mrp">{money(product.original_price || calcMrp(product.price, product.off))}</s>
                    )}
                  </div>
                  {product.off && <div className="fc-off">{product.off}</div>}
                  <button className="fc-add-btn" onClick={() => onAdd(product)} data-testid={`${id}-add-${product.id}`}>
                    <ShoppingCart size={13} /> Add to Cart
                  </button>
                </div>
              </article>
            </div>
          ))}
        </div>
      </div>
      {products.length > 1 && (
        <div className="shelf-dots" data-testid={`${id}-dots`}>
          {products.map((_, i) => (
            <button key={i} className={`shelf-dot${i === idx ? " active" : ""}`} onClick={() => setIdx(i)} data-testid={`${id}-dot-${i}`} />
          ))}
        </div>
      )}
    </motion.section>
  );
}

function MajorSaleSections({ products, onAdd, sections = [] }) {
  const sales = [
    { id: "mobile-parts-deals", title: "Mobile Parts Deals", eyebrow: "REPAIR ESSENTIALS", icon: Wrench, category: "accessories", tone: "blue" },
    { id: "mobile-body-parts", title: "Mobile Body Parts", eyebrow: "ORIGINAL PARTS", icon: Wrench, category: "components", tone: "teal" },
    { id: "today-deals", title: "Today's Deals", eyebrow: "ENDS TONIGHT", icon: Flame, category: "mobiles", tone: "orange", showTimer: true },
    { id: "deal-of-the-day", title: "Deal of the Day", eyebrow: "FEATURED PICK", icon: Clock, category: "", tone: "purple", showTimer: true },
    { id: "new-stock", title: "New Stock", eyebrow: "JUST LANDED", icon: Smartphone, category: "", tone: "green" },
    { id: "stock-clearance", title: "Stock Clearance Sale", eyebrow: "LAST CHANCE", icon: Percent, category: "", tone: "red" },
    { id: "chargers-cables", title: "Mobile Chargers & Cables", eyebrow: "FAST CHARGING", icon: Zap, category: "accessories", tone: "cyan" },
  ];
  const shelfProducts = (category, offset) => {
    const saleId = sales[offset]?.id;
    const assigned = products.filter((product) => product.homepage_sections?.includes(saleId));
    if (assigned.length) return [...assigned.slice(offset), ...assigned.slice(0, offset)].slice(0, 6);
    const preferred = category ? products.filter((product) => product.category_slug === category) : products;
    const source = [...preferred, ...products.filter((product) => !preferred.some((item) => item.id === product.id))];
    return [...source.slice(offset), ...source.slice(0, offset)].slice(0, 6);
  };
  return (
    <div className="major-sale-sections" data-testid="major-sale-sections">
      {sales.map((sale, index) => {
        const setting = sections.find((section) => section.id === sale.id) || {};
        if (setting.active === false) return null;
        return <SaleShelf key={sale.id} {...sale} title={setting.title || sale.title} eyebrow={setting.eyebrow || sale.eyebrow} products={shelfProducts(sale.category, index)} onAdd={onAdd} to={`/category?section=${sale.id}`} />;
      })}
    </div>
  );
}


/* ========== Glass Product Card ========== */
function GlassProductCard({ product, onAdd, onWish, badge, index = 0 }) {
  if (!product) return null;
  const rating = rateFor(product.id);
  const outOfStock = product.stock <= 0;
  return (
    <motion.article className="product-card" variants={staggerItem} data-testid={`product-card-${product.id}`}
      whileHover={{ y: -6, transition: { duration: 0.22 } }}>
      {outOfStock ? <span className="stock-badge out" data-testid={`out-of-stock-${product.id}`}>OUT OF STOCK</span> : badge && <span className="card-badge">{badge}</span>}
      <div className="product-media-wrap">
        <Link to={`/product/${product.id}`} data-testid={`product-link-${product.id}`} className="product-media">
          <img src={product.image || PLACEHOLDER_IMAGE} alt={product.name} loading="lazy" onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMAGE; }} />
        </Link>
        <button className="wish" onClick={() => onWish(product.id)} data-testid={`wishlist-${product.id}`} aria-label={`Save ${product.name}`}><Heart size={15} /></button>
      </div>
      <div className="product-info">
        <Link to={`/product/${product.id}`}><h4 className="pc-name">{product.name}</h4></Link>
        <p className="pc-sub">{product.sub}</p>
        <div className="rating-line"><span className="stars"><Star size={11} fill="currentColor" /> {rating.stars}</span><small>({rating.count})</small></div>
        <div className="price-row">
          <strong>{money(product.price)}</strong>
          {product.old > product.price && <del>{money(product.old)}</del>}
          {product.off && <em className="off-pill">{product.off}</em>}
        </div>
      </div>
      <motion.button className="add-cart-btn" disabled={outOfStock} onClick={() => onAdd(product)} data-testid={`add-product-${product.id}`}
        whileTap={{ scale: 0.96 }}>{outOfStock ? "Out of Stock" : <><ShoppingCart size={13} /> Add to Cart</>}</motion.button>
    </motion.article>
  );
}

/* ========== Trust Strip ========== */
function TrustStrip() {
  const items = [
    [ShieldCheck, "100% Original", "Products"],
    [Wallet, "Secure Payments", "UPI | Card | Wallet"],
    [Truck, "Fast Shipping", "Pan India"],
    [RotateCcw, "Easy Returns", "Within 7 Days"],
    [Headphones, "24x7 Support", "Always Here"],
    [BadgeCheck, "Best Wholesale Prices", "For Businesses"],
  ];
  return (
    <motion.div {...fadeUp} className="f-trust-strip">
      {items.map(([Icon, title, sub]) => (
        <div key={title} className="f-trust-item">
          <Icon size={18} className="f-trust-icon" />
          <span className="f-trust-text"><b>{title}</b><small>{sub}</small></span>
        </div>
      ))}
    </motion.div>
  );
}

/* ========== Hot Selling Categories ========== */
function HotSellingCategories({ title = "Hot Selling Categories" }) {
  return (
    <motion.section {...fadeUp} style={{ margin: "30px 0" }} data-testid="hot-selling-categories">
      <div className="section-title"><h2><Flame size={20} style={{ color: "var(--gold)", marginRight: 8 }} />{title}</h2></div>
      <div className="f-hot-cats">
        {HOT_CATEGORIES.map(c => {
          const Icon = c.icon;
          return (
            <motion.div key={c.label} whileHover={{ y: -3 }}>
              <Link to={c.slug ? `/category?category=${c.slug}` : "/category"} className="f-hot-cat" data-testid={`hot-cat-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
                <Icon size={20} />
                <span><b>{c.label}</b><small>{c.sub}</small></span>
                <ChevronRight size={14} style={{ color: "#6b7f9e", marginLeft: "auto" }} />
              </Link>
            </motion.div>
          );
        })}
      </div>
    </motion.section>
  );
}

/* ========== Quad Promo (Auction, Wholesale, Trade-In, Price Drop) ========== */
function QuadPromo() {
  return (
    <motion.div {...fadeUp} className="f-quad-grid" data-testid="futuristic-quad-grid">
      <Link to="/auctions" className="f-quad-card f-quad-auction" data-testid="quad-auction-zone">
        <Gavel size={22} style={{ color: "var(--neon-purple)", marginBottom: 8 }} />
        <h3>Auction Zone</h3>
        <p>Live Bidding | Real Deals</p>
        <span className="f-promo-btn">Join Auction <ArrowRight size={12} /></span>
      </Link>
      <Link to="/category" className="f-quad-card f-quad-wholesale" data-testid="quad-wholesale">
        <PackageOpen size={22} style={{ color: "var(--neon-blue)", marginBottom: 8 }} />
        <h3>Wholesale for Business</h3>
        <p>Bulk Orders | Special Pricing | Dedicated Support</p>
        <span className="f-promo-btn">Apply for Wholesale <ArrowRight size={12} /></span>
      </Link>
      <Link to="/sell" className="f-quad-card f-quad-trade" data-testid="quad-trade-in">
        <RefreshCw size={22} style={{ color: "var(--neon-green)", marginBottom: 8 }} />
        <h3>Trade-In</h3>
        <p>Upgrade with Benefits</p>
        <span className="f-promo-btn">Exchange Now <ArrowRight size={12} /></span>
      </Link>
      <Link to="/category?sort=price_desc" className="f-quad-card f-quad-radar" data-testid="quad-price-radar">
        <TrendingUp size={22} style={{ color: "var(--gold)", marginBottom: 8 }} />
        <h3>Price Drop Radar</h3>
        <p>Track Price Drops & Grab the Best Deals</p>
        <span className="f-promo-btn">Explore Now <ArrowRight size={12} /></span>
      </Link>
    </motion.div>
  );
}

/* ========== Trending + Why Choose + Reviews ========== */
function TrendingReviewsSection({ products, onAdd, onWish }) {
  const [tab, setTab] = useState("All");
  const tabs = ["All", "Mobiles", "Parts", "Accessories"];
  const filtered = products.filter(p => tab === "All" || p.category_slug === tab.toLowerCase());
  const display = (filtered.length ? filtered : products).slice(0, 4);
  return (
    <motion.div {...fadeUp} className="f-triple-grid f-two-col" data-testid="trending-reviews-section">
      <section>
        <div className="section-title" style={{ flexWrap: "wrap" }}>
          <h2>Trending Products</h2>
          <div className="pill-tabs">
            {tabs.map(t => <button key={t} className={tab === t ? "on" : ""} onClick={() => setTab(t)} data-testid={`trending-tab-${t.toLowerCase()}`}>{t}</button>)}
          </div>
          <Link to="/category" data-testid="trending-view-all">View All <ChevronRight size={15} /></Link>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
          {display.map(p => (
            <Link to={`/product/${p.id}`} key={p.id} data-testid={`trending-product-${p.id}`} className="f-trend-item">
              <img src={p.image} alt={p.name} className="f-trend-img" />
              <div><b className="f-trend-name">{p.name}</b><strong className="f-trend-price">{money(p.price)}</strong></div>
            </Link>
          ))}
        </div>
      </section>

      <section data-testid="why-choose-section">
        <div className="section-title"><h2 style={{ fontSize: 16 }}>Why Choose DMMobile Shop?</h2></div>
        <div style={{ display: "grid", gap: 8 }}>
          {[["100% Original Products", "No Fake, No Duplicate"], ["Best Prices", "Wholesale & Retail"], ["Fast & Safe Delivery", "Pan India"], ["Easy Returns", "Hassle Free"], ["Dedicated Support", "24x7 Help"]].map(([t, s]) => (
            <div key={t} className="f-why-item">
              <ShieldCheck size={14} className="f-why-icon" />
              <span><b className="f-why-title">{t}</b><br /><small className="f-why-sub">{s}</small></span>
            </div>
          ))}
        </div>
      </section>
    </motion.div>
  );
}

/* ========== Blog Section ========== */
function BlogSection() {
  const blogs = [
    { title: "Best Smartphones Under 30000", desc: "Our top picks for the best value phones this year." },
    { title: "iPhone vs Samsung: Which is Better?", desc: "A detailed comparison of the two biggest rivals." },
    { title: "Mobile Accessories You Must Have", desc: "Essential accessories every smartphone owner needs." },
  ];
  return (
    <motion.section {...fadeUp} data-testid="blog-section">
      <div className="section-title"><h2>Latest from Our Blog</h2></div>
      <div className="f-blog-grid">
        {blogs.map(b => (
          <Link to="/category" className="f-blog-card" key={b.title} data-testid={`blog-${b.title.toLowerCase().replace(/\s/g, "-").slice(0, 20)}`}>
            <div className="f-blog-img"><Globe size={30} /></div>
            <h4>{b.title}</h4>
            <p>{b.desc}</p>
            <small style={{ color: "var(--neon-blue)", fontSize: 10, fontWeight: 700, marginTop: 8, display: "inline-flex", alignItems: "center", gap: 4 }}>Read More <ChevronRight size={12} /></small>
          </Link>
        ))}
      </div>
    </motion.section>
  );
}

/* ========== Community + WhatsApp + Newsletter ========== */
function BottomSections() {
  return (
    <motion.div {...fadeUp} className="f-bottom-grid" data-testid="bottom-sections">
      <div className="f-community-card" data-testid="community-section">
        <h3>Join Our Community</h3>
        <p>Follow us for latest deals, updates & offers</p>
        <div className="f-social-links">
          {["YT", "IG", "FB", "TW", "TG"].map(s => <a key={s} href="#" data-testid={`social-${s.toLowerCase()}`}><MessageCircle size={16} /></a>)}
        </div>
      </div>
      <div className="f-whatsapp-card" data-testid="whatsapp-section">
        <Phone size={22} style={{ color: "var(--neon-green)", marginBottom: 8 }} />
        <h3>Get Exclusive Offers</h3>
        <p>Join WhatsApp Channel for Latest Deals | New Arrivals | Exclusive Offers</p>
        <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} style={{ padding: "10px 18px", borderRadius: 10, border: "none", background: "var(--neon-green)", color: "#0a2015", fontWeight: 800, fontSize: 12, cursor: "pointer" }} data-testid="join-whatsapp-btn">Join Now <ChevronRight size={13} /></motion.button>
      </div>
      <div className="f-newsletter-card" data-testid="newsletter-section">
        <h3>Subscribe to Our Newsletter</h3>
        <p>Get the latest deals, offers and updates.</p>
        <div className="f-newsletter-form">
          <input placeholder="Enter your email address" data-testid="newsletter-email-input" />
          <button data-testid="newsletter-subscribe-btn">Subscribe</button>
        </div>
      </div>
    </motion.div>
  );
}

/* ========== Enhanced Footer ========== */
function FuturisticFooter() {
  return (
    <footer className="f-footer" data-testid="futuristic-footer">
      <div className="f-footer-grid">
        <div className="f-footer-col">
          <div className="f-footer-brand">
            <span className="f-footer-brand-icon"><Smartphone size={20} /></span>
            <div><b>DMMobile Shop</b><small>Mobiles | Parts | Accessories | Wholesale<br />Your Trusted Mobile & Parts Partner</small></div>
          </div>
          <p className="f-footer-desc">DMMobile Shop is your one-stop destination for genuine mobiles, parts, accessories and wholesale solutions. We deliver quality products at the best prices across India.</p>
        </div>
        <div className="f-footer-col">
          <h4>Quick Links</h4>
          {["Home", "Mobiles", "Parts", "Accessories", "Wholesale", "Deals"].map(l => <Link key={l} to={l === "Home" ? "/" : "/category"} data-testid={`footer-link-${l.toLowerCase()}`}>{l}</Link>)}
        </div>
        <div className="f-footer-col">
          <h4>Customer Support</h4>
          {["Help Center", "Track Order", "Return & Refund", "Shipping Policy", "FAQ", "Contact Us"].map(l => <Link key={l} to="/policies/shipping" data-testid={`footer-link-${l.toLowerCase().replace(/\s/g, "-")}`}>{l}</Link>)}
        </div>
        <div className="f-footer-col">
          <h4>Our Services</h4>
          {["Bulk Orders", "Wholesale", "Exchange", "EMI Options", "Warranty Policy", "Price Match"].map(l => <Link key={l} to="/category" data-testid={`footer-link-${l.toLowerCase().replace(/\s/g, "-")}`}>{l}</Link>)}
        </div>
        <div className="f-footer-col">
          <h4>Download Our App</h4>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <span style={{ padding: "8px 14px", borderRadius: 8, background: "rgba(15,25,45,0.6)", border: "1px solid var(--glass-border)", color: "#c0d0eb", fontSize: 10 }}>Google Play</span>
            <span style={{ padding: "8px 14px", borderRadius: 8, background: "rgba(15,25,45,0.6)", border: "1px solid var(--glass-border)", color: "#c0d0eb", fontSize: 10 }}>App Store</span>
          </div>
          <small style={{ color: "#6b7f9e", fontSize: 9 }}>Get exclusive offers & faster ordering</small>
        </div>
      </div>
      <div className="f-footer-bottom">
        <span>&copy; 2080 DMMobile Shop. All rights reserved.</span>
        <div>
          <Link to="/policies/terms" data-testid="footer-terms">Terms & Conditions</Link>
          <Link to="/policies/privacy" data-testid="footer-privacy">Privacy Policy</Link>
        </div>
      </div>
    </footer>
  );
}

/* ========== Compare iPhones Section ========== */
function CompareSection({ products = [] }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  // Use real DB products; if none, don't render compare cards
  const compareProducts = products.slice(0, 4).map((p, i) => ({
    name: p.name,
    price: `₹${Number(p.price).toLocaleString("en-IN")}`,
    storage: p.storage_options?.join(" / ") || "64GB",
    warranty: p.warranty_days ? `${p.warranty_days} days` : "—",
    grade: p.qc_grade || "A",
    tag: p.tag || (i === 0 ? "Best Seller" : i === 1 ? "Best Value" : "Popular"),
    image: p.images?.[0] ? (p.images[0].startsWith("http") ? p.images[0] : `${process.env.REACT_APP_BACKEND_URL}${p.images[0]}`) : null,
    id: p.id,
  }));

  const SPECS = [
    { label: "Starting Price", key: "price" },
    { label: "Storage", key: "storage" },
    { label: "QC Grade", key: "grade" },
    { label: "Warranty", key: "warranty" },
  ];

  return (
    <motion.section ref={ref} {...fadeUp} className="phi-compare" data-testid="compare-section">
      <div className="phi-section-head">
        <span className="phi-section-eyebrow">COMPARE PRODUCTS</span>
        <h2>Find the Right Phone for You</h2>
        <p>Compare price, storage & value across available models</p>
      </div>
      {compareProducts.length === 0 ? (
        <div className="compare-empty" style={{ textAlign: "center", padding: "32px", color: "#6b7f9e", fontSize: 14 }}>
          <Smartphone size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
          <p>Products load ho rahe hain ya abhi available nahi hain.</p>
          <Link to="/category?category=mobiles" className="phi-btn-primary" style={{ marginTop: 12, display: "inline-block" }}>Browse All Products</Link>
        </div>
      ) : (
        <div className="phi-compare-grid">
          {compareProducts.map((phone) => (
            <motion.div
              key={phone.id}
              className="phi-compare-card"
              whileHover={{ y: -6 }}
              transition={{ duration: 0.22 }}
              data-testid={`compare-card-${phone.id}`}
            >
              <div className="phi-cc-header">
                {phone.image
                  ? <img src={phone.image} alt={phone.name} className="phi-cc-img" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  : <div className="phi-cc-img-placeholder"><Smartphone size={36} /></div>
                }
                <span className="phi-cc-name">{phone.name}</span>
                <span className="phi-cc-price">{phone.price}</span>
                <span className="phi-cc-badge">{phone.tag}</span>
              </div>
              <ul className="phi-cc-specs">
                {SPECS.map((spec) => (
                  <li key={spec.key} className="phi-cc-spec-row">
                    <span className="label">{spec.label}</span>
                    <span className="value">{phone[spec.key]}</span>
                  </li>
                ))}
              </ul>
              <Link to={`/product/${phone.id}`} className="phi-btn-primary" style={{ display: "block", textAlign: "center", marginTop: 12 }}>
                View Deal
              </Link>
            </motion.div>
          ))}
        </div>
      )}
      <div className="phi-compare-actions">
        <Link to="/category?category=mobiles" className="phi-btn-primary" data-testid="compare-shop-btn">Shop All iPhones</Link>
        <Link to="/sell" className="phi-btn-outline" data-testid="compare-sell-btn">Sell Your iPhone</Link>
      </div>
    </motion.section>
  );
}

/* ========== 3D iPHONE BANNER ========== */
const BRAND_SLIDES_DEFAULT = [
  { headline: "Premium iPhones", highlight: "Up to 40% OFF", sub: "Certified · Tested · Trusted — 50-Point QC", cta: "Shop iPhones", link: "/category", accent: "#00d4ff" },
  { headline: "Sell Your iPhone", highlight: "Best Price Guaranteed", sub: "Get top ₹ in your wallet within 24 hours", cta: "Sell Now →", link: "/sell", accent: "#00e5a0" },
  { headline: "Live Auctions", highlight: "Bid & Win", sub: "Join live auctions on verified used iPhones", cta: "Join Auction", link: "/auctions", accent: "#f59e0b" },
];

const IPHONE_PRO_IMG = "https://static.prod-images.emergentagent.com/jobs/4d8ba7d6-4cc2-48fd-a329-88470c3b0d75/images/180fee2348bb10af2e1a019202be2a88f4da8601bd9f142b0cb84cfefcde1a06.jpeg";

function DMBanner3D() {
  const [slides, setSlides] = useState(BRAND_SLIDES_DEFAULT);
  const [slide, setSlide] = useState(0);
  const [entered, setEntered] = useState(false);

  // Fetch admin-managed banner slides
  useEffect(() => {
    api.get("/api/homepage/banners")
      .then(({ data }) => { if (data?.length >= 1) setSlides(data); })
      .catch(() => {});
  }, []);

  const total = slides.length;

  useEffect(() => {
    const t = setInterval(() => setSlide(s => (s + 1) % total), 4500);
    return () => clearInterval(t);
  }, [total]);

  useEffect(() => {
    const id = setTimeout(() => setEntered(true), 120);
    return () => clearTimeout(id);
  }, []);

  const sl = slides[slide];

  return (
    <div className="dm3d-banner" data-testid="iphone-3d-banner">
      {/* Ambient BG */}
      <div className="dm3d-bg">
        <div className="dm3d-glow1" style={{ background: `radial-gradient(circle, ${sl.accent}28 0%, transparent 65%)` }} />
        <div className="dm3d-glow2" />
        <div className="dm3d-grid" />
      </div>
      <div className="dm3d-particles" aria-hidden>
        {[...Array(12)].map((_, i) => <span key={i} className={`dm3d-p dm3d-p${i}`} />)}
      </div>

      {/* Left: Branding + Slide Text */}
      <div className="dm3d-left">
        <div className="dm3d-brand" data-testid="banner-brand">
          <span className="dm3d-brand-icon">DM</span>
          <div>
            <b>DMMobile Shop</b>
            <small>India's Trusted iPhone Marketplace</small>
          </div>
        </div>

        <div className="dm3d-slide-text" key={slide} data-testid="banner-slide-text">
          <span className="dm3d-eyebrow" style={{ color: sl.accent }}>
            <i className="dm3d-dot" style={{ background: sl.accent }} />
            VERIFIED DEVICES · FREE DELIVERY
          </span>
          <h2 className="dm3d-heading">{sl.headline}</h2>
          <p className="dm3d-highlight" style={{ color: sl.accent }}>{sl.highlight}</p>
          <p className="dm3d-sub">{sl.sub}</p>
          <Link
            to={sl.link}
            className="dm3d-cta"
            style={{ background: sl.accent, color: "#060d1a", fontWeight: 900 }}
            data-testid="banner-cta-button"
          >
            {sl.cta} →
          </Link>
        </div>

        <div className="dm3d-footer-row">
          <div className="dm3d-trust">
            <span>✓ 50-Point QC</span>
            <span>✓ 6M Warranty</span>
            <span>✓ Easy Returns</span>
          </div>
          <div className="dm3d-dots">
            {slides.map((_, i) => (
              <button
                key={i}
                className={`dm3d-dot-btn${i === slide ? " on" : ""}`}
                style={i === slide ? { background: sl.accent, width: 18 } : undefined}
                onClick={() => setSlide(i)}
                data-testid={`banner-dot-${i}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Right: Premium iPhone Photo with 3D transforms */}
      <div className="dm3d-right">
        <div className="dm3d-scene">
          {/* Orbit rings */}
          <div className="dm3d-ring dm3d-ring1" style={{ borderColor: `${sl.accent}30` }} />
          <div className="dm3d-ring dm3d-ring2" />
          <div className="dm3d-ring dm3d-ring3" />

          {/* Accent glow */}
          <div className="dm3di-bg-glow" style={{ background: `radial-gradient(ellipse 75% 60% at 50% 55%, ${sl.accent}20 0%, transparent 70%)` }} />

          {/* Premium iPhone – approach then float */}
          <div className={`dm3d-iphone${entered ? " dm3d-iphone--on" : ""}`} data-testid="banner-3d-phone">
            <div className="dm3d-iphone-inner">
              <div className="dm3d-phone-real-wrap">
                <img
                  src={IPHONE_PRO_IMG}
                  alt="iPhone 15 Pro"
                  className="dm3d-phone-real-img"
                  onError={(e) => { e.currentTarget.style.opacity = "0.15"; }}
                />
                <div
                  className="dm3d-phone-real-glow"
                  style={{ boxShadow: `0 0 90px ${sl.accent}30, 0 50px 100px rgba(0,0,0,0.75)` }}
                />
              </div>
            </div>
          </div>

          {/* Floating spec badges */}
          <div className="dm3d-badge dm3d-badge1" data-testid="banner-badge-verified"><span>✓</span> Verified</div>
          <div className="dm3d-badge dm3d-badge2"><span>★</span> 4.9 Rating</div>
          <div className="dm3d-badge dm3d-badge3"><span>⚡</span> 10K+ Sold</div>
        </div>
      </div>
    </div>
  );
}

export function FuturisticHome({ products, auctions, onAdd, onWish, common, announcements = [], homeConfig = null, Topbar, FooterBar, BottomNav, Loading }) {
  const navigate = useNavigate();
  const display = useMemo(() => [...products].sort((a, b) => (FEATURED_ORDER.indexOf(a.id) + 99) % 99 - (FEATURED_ORDER.indexOf(b.id) + 99) % 99), [products]);
  const primary = display[0] || null;
  const heroDeals = display.slice(0, 3);
  const flashProducts = display.filter((product) => product.homepage_sections?.includes("flash-deals"));
  const sections = homeConfig?.sections || [];
  const section = (id) => sections.find((item) => item.id === id) || {};
  const isVisible = (id) => section(id).active !== false;
  const hero = homeConfig?.hero || {};

  if (!display.length) return <><Topbar {...common} /><main className="store-page"><Loading /></main><FooterBar /></>;

  return (
    <>
      <Topbar {...common} />

      {/* Announcement Strip */}
      {announcements.length > 0 && (
        <section className="announcement-strip" data-testid="website-announcement-strip">
          <div className="announcement-label" data-testid="website-announcement-label"><Smartphone size={14} /><span>LIVE UPDATE</span></div>
          <div className="announcement-viewport"><div className="announcement-track"><div className="announcement-group">{announcements.slice(0, 6).map(a => <Link to="/category" key={a.id} data-testid={`announcement-${a.id}`}><Smartphone size={13} /><b>{a.title}</b>{a.description && <small>{a.description}</small>}</Link>)}</div><div className="announcement-group announcement-copy" aria-hidden="true">{announcements.slice(0, 6).map(a => <span key={`copy-${a.id}`}><Smartphone size={13} /><b>{a.title}</b>{a.description && <small>{a.description}</small>}</span>)}</div></div></div>
        </section>
      )}

      <main className="store-page futuristic-home marketplace-home" data-testid="futuristic-home">
        {/* Stage: Category Rail + Hero */}
        <div className="marketplace-stage">
          <aside className="marketplace-category-rail" data-testid="marketplace-category-rail">
            <b>Top Categories</b>
            {CATEGORIES.map(c => {
              const Icon = c.icon;
              return <Link key={c.slug + c.label} to={`/category?category=${c.slug}`} data-testid={`marketplace-rail-${c.slug}`}><Icon size={15} /><span>{c.label}</span></Link>;
            })}
            <Link to="/auctions" className="rail-auction" data-testid="marketplace-rail-auctions"><Gavel size={15} /><span>Live Auction</span></Link>
            <Link to="/category?sort=price_desc" className="rail-deals" data-testid="marketplace-rail-deals"><Percent size={15} /><span>Deals & Offers</span></Link>
          </aside>

          {/* Hero + Side Cards */}
          <section className="hero-grid">
            <DMBanner3D />

            {/* Right side cards */}
            <aside className="hot-deals" data-testid="hot-deals">
              <div className="hot-head">
                <b style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "var(--neon-green)", boxShadow: "0 0 10px var(--neon-green)", animation: "neonPulse 2s infinite" }} />
                  Live Auction
                </b>
                <Link to="/auctions" data-testid="hot-deals-view-all">Join Now <ChevronRight size={13} /></Link>
              </div>
              {auctions[0] && (
                <div style={{ textAlign: "center", padding: "8px 0" }}>
                  <img src={auctions[0].product?.image} alt={auctions[0].product?.name} style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 12, margin: "0 auto 8px", display: "block", background: "rgba(10,18,35,0.5)" }} />
                  <b style={{ fontSize: 12, color: "#e2eafc" }}>{auctions[0].product?.name}</b>
                  <strong style={{ display: "block", fontSize: 18, color: "var(--neon-cyan)", margin: "4px 0" }}>{money(auctions[0].current_bid)}</strong>
                  <small style={{ color: "#8fa5c4", fontSize: 10 }}>Current Bid</small>
                </div>
              )}
              <div style={{ borderTop: "1px solid rgba(100,160,255,0.06)", paddingTop: 10, marginTop: 8 }}>
                <div className="hot-head"><b>Wholesale</b><Link to="/category" data-testid="wholesale-link">Start Now <ChevronRight size={13} /></Link></div>
                <small style={{ color: "#8fa5c4", fontSize: 10 }}>Best Rates for Businesses</small>
              </div>
            </aside>
          </section>
        </div>

        {/* Category navigation — directly below the hero banner */}
        {isVisible("category-navigation") && <motion.section {...fadeUp} className="circle-row" data-testid="category-circles">
          {CATEGORIES.slice(0, 10).map(c => {
            const Icon = c.icon;
            return (
              <Link key={c.label} to={`/category?category=${c.slug}`} className="circle-cat" data-testid={`circle-${c.label.toLowerCase().replace(/\s|&/g, "-")}`}>
                <motion.span className="circle-icon" whileHover={{ scale: 1.1, y: -4 }}><Icon size={22} /></motion.span>
                <small>{c.label.split(" ")[0]}</small>
              </Link>
            );
          })}
        </motion.section>}

        {/* Sale Shelves — directly below the hero banner */}
        {isVisible("flash-deals") && <SaleShelf id="flash-deals" title={section("flash-deals").title || "Flash Deals"} eyebrow={section("flash-deals").eyebrow || "LIMITED TIME"} icon={Flame} products={(flashProducts.length ? flashProducts : display).slice(0, 6)} onAdd={onAdd} to="/category?section=flash-deals" tone="pink" showTimer />}
        <MajorSaleSections products={display} onAdd={onAdd} sections={sections} />

        {/* Trust Strip */}
        {isVisible("trust-strip") && <TrustStrip />}

        {/* Hot Selling Categories */}
        {isVisible("hot-categories") && <HotSellingCategories title={section("hot-categories").title || "Hot Selling Categories"} />}

        {/* Quad Promo: Auction, Wholesale, Trade-In, Price Drop */}
        {isVisible("marketplace-services") && <QuadPromo />}

        {/* Promo Banners */}
        {isVisible("promo-banners") && <motion.section {...fadeUp} className="promo-row" data-testid="promo-banners">
          <div className="promo promo-a" data-testid="promo-iphone">
            <div><b>Biggest iPhone Deals</b><span>Up to <em>40% OFF</em></span><Link to="/category?category=mobiles" className="promo-btn">Shop iPhones <ChevronRight size={13} /></Link></div>
            {primary?.image && <img src={primary.image} alt="iPhone deals" />}
          </div>
          <div className="promo promo-b" data-testid="promo-laptops">
            <div><b>Laptops for Work & Play</b><span>Top Brands. Great Prices.</span><Link to="/category?category=laptops" className="promo-btn">Explore Laptops <ChevronRight size={13} /></Link></div>
          </div>
          <div className="promo promo-c" data-testid="promo-preowned">
            <div><b>Certified Pre-Owned</b><span>Same Performance. Better Value.</span><Link to="/category" className="promo-btn">Shop Pre-Owned <ChevronRight size={13} /></Link></div>
          </div>
        </motion.section>}

        {/* Trending + Why Choose + Reviews */}
        {isVisible("product-insights") && <TrendingReviewsSection products={display} onAdd={onAdd} onWish={onWish} />}

        {/* Auction Teaser */}
        {isVisible("auction-teaser") && auctions[0] && (
          <motion.section {...fadeScale} className="auction-teaser" data-testid="auction-teaser">
            <div className="auction-teaser-copy">
              <span className="live-pill">LIVE AUCTION</span>
              <h3>{auctions[0].product?.name}</h3>
              <small>Current Bid</small>
              <strong>{money(auctions[0].current_bid)}</strong>
              <Link to="/auctions" className="primary-btn" data-testid="auction-teaser-bid">Bid Now <Gavel size={14} /></Link>
            </div>
            {auctions[0].product?.image && <img src={auctions[0].product.image} alt="Auction" />}
          </motion.section>
        )}

        {/* iPhone Compare Section */}
        <CompareSection products={display} />

        {/* Premium iPhone Sections: Why Buy, Scroll Story, Sell CTA, Testimonials, Stats */}
        <PremiumHeroSection mode="sections-only" />

        {/* Blog */}
        {isVisible("blog") && <BlogSection />}

        {/* Community + WhatsApp + Newsletter */}
        {isVisible("customer-community") && <BottomSections />}
      </main>

      <FuturisticFooter />
      <BottomNav cartCount={common.cartCount} orderCount={common.activeOrderCount} />
    </>
  );
}

